/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import type { CSSProperties } from 'react';
import { Icon } from './ComponentIcon';

export type AppLogoApp = {
  code?: string;
  name?: string;
  icon?: string;
  iconSvg?: string;
};

export type AppLogoProps = {
  app?: AppLogoApp;
  sourceCode?: string | null;
  size?: number;
  color?: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
};

const SOURCE_APP_REGISTRY_ALIASES: Record<string, string> = {
  APPS: 'CORE',
};

export function normalizeSourceAppCode(sourceApp?: string | null): string {
  return String(sourceApp ?? '').trim().toUpperCase();
}

export function getRegistryCodeForSourceApp(sourceApp?: string | null): string {
  const code = normalizeSourceAppCode(sourceApp);
  return SOURCE_APP_REGISTRY_ALIASES[code] ?? code;
}

export function normalizeAppLogoSvg(html: string): string {
  const withoutXml = html
    .replace(/^\s*<\?xml[^?]*\?>\s*/i, '')
    .replace(/(fill|stroke)\s*=\s*"(?!\s*(?:none|currentColor)\s*")[^"]*"/gi, '$1="currentColor"')
    .replace(/(fill|stroke)\s*=\s*'(?!\s*(?:none|currentColor)\s*')[^']*'/gi, "$1='currentColor'")
    .replace(/(fill|stroke)\s*:\s*(?!\s*(?:none|currentColor)\s*[;"'])[^;"']+/gi, '$1:currentColor')
    .trim();

  return withoutXml.replace(/<svg\b([^>]*)>/i, (_match, attrs: string) => {
    const nextAttrs = String(attrs ?? '')
      .replace(/\s(width|height|focusable|aria-hidden)\s*=\s*"[^"]*"/gi, '')
      .replace(/\s(width|height|focusable|aria-hidden)\s*=\s*'[^']*'/gi, '')
      .trim();
    return `<svg${nextAttrs ? ` ${nextAttrs}` : ''} width="100%" height="100%" focusable="false" aria-hidden="true">`;
  });
}

function fallbackText(sourceCode?: string | null): string {
  const code = normalizeSourceAppCode(sourceCode);
  if (!code) return '?';
  return code.replace(/[^A-Z0-9]/g, '').slice(0, 2) || '?';
}

export function AppLogo({
  app,
  sourceCode,
  size = 18,
  color,
  className = '',
  style,
  title,
}: AppLogoProps) {
  const rawSvg = app?.iconSvg?.trim();
  const mergedStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: `${size}px`,
    height: `${size}px`,
    minWidth: `${size}px`,
    color: color || 'currentColor',
    lineHeight: 0,
    flex: '0 0 auto',
    ...style,
  };

  if (rawSvg) {
    return (
      <span className={`qp-app-logo ${className}`.trim()} style={mergedStyle} title={title} aria-hidden={title ? undefined : true}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            lineHeight: 0,
          }}
          dangerouslySetInnerHTML={{ __html: normalizeAppLogoSvg(rawSvg) }}
          aria-hidden
        />
      </span>
    );
  }

  if (app?.icon) {
    return (
      <span className={`qp-app-logo ${className}`.trim()} style={mergedStyle} title={title} aria-hidden={title ? undefined : true}>
        <Icon name={app.icon} size={size} color={color} />
      </span>
    );
  }

  return (
    <span className={`qp-app-logo ${className}`.trim()} style={mergedStyle} title={title} aria-hidden={title ? undefined : true}>
      <span
        style={{
          fontSize: `${Math.max(9, Math.round(size * 0.46))}px`,
          fontWeight: 700,
          lineHeight: 1,
          letterSpacing: 0,
        }}
      >
        {fallbackText(sourceCode)}
      </span>
    </span>
  );
}
